import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import FullWidthCarouselWithAnalytics from 'components/WithAnalytics/Carousel/FullWidthCarouselWithAnalytics'
import useOfferList from 'hooks/Offers/useOfferList'
import type { ComponentProps, PropsWithChildren } from 'react'
import React, { useEffect, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import OfferListCarouselTrack from './OfferListCarouselTrack'

type InheritedOfferListCarouselTrackProps = Pick<
  ComponentProps<typeof OfferListCarouselTrack>,
  'additionalOfferUrlQuery' | 'tabletLimit' | 'limit' | 'tileStyle' | 'tileSize' | 'eagerLoadImages'
>

interface Props extends PropsWithChildren, InheritedOfferListCarouselTrackProps {
  /**
   * The set of filters to fetch the offer list with
   */
  filters?: App.OfferListFilters
  /**
   * Use this to provide your own full offer list for the carousel to display
   */
  overrideOfferList?: App.OfferList
  className?: string
  /**
   * Exclude the following offer ids from the results
   */
  excludedIds?: Array<string>
  /**
   * - `block`: To be used when the carousel is contained inside another element. (ie. modal or dropdown)
   * - `page`: To be used as an immediate child of a page. (like a page section)
   *
   * @default page
   */
  size?: 'block' | 'page'
  /**
   * Override the duration and the price with the checkIn/checkOut/Occupancy
   */
  selectedDatesAndRoomsFilter?: App.OfferListFilters
}

function OfferListCarousel(props: Props) {
  const {
    filters,
    overrideOfferList,
    limit,
    tabletLimit,
    children,
    tileStyle = 'card-default',
    tileSize = 'small',
    additionalOfferUrlQuery,
    className,
    excludedIds,
    size = 'page',
    selectedDatesAndRoomsFilter,
    eagerLoadImages,
  } = props

  const filterOfferList = useOfferList(
    filters,
    {
      disabled: !!overrideOfferList || !filters,
      excludedIds,
      minRequiredOffers: filters?.limit,
    },
  )
  const offerList = overrideOfferList ?? filterOfferList
  const carouselRef = useRef<HTMLDivElement | null>(null)

  const [inViewRef, carouselInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  useEffect(() => {
    // We've been given a new offer list, send the carousel back to the start
    // Only scroll when data is ready and the carousel is mounted
    if (!offerList.fetching && carouselRef.current) {
      carouselRef.current.scrollTo({ left: 0 })
    }
  }, [offerList.fetching, offerList.key, offerList.offerIds])

  useEffect(() => {
    inViewRef(carouselRef.current)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRef.current])

  const track = <OfferListCarouselTrack
    inView={carouselInView}
    offerList={offerList}
    tileStyle={tileStyle}
    tileSize={tileSize}
    additionalOfferUrlQuery={additionalOfferUrlQuery}
    limit={limit}
    tabletLimit={tabletLimit}
    selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
    eagerLoadImages={eagerLoadImages}
  >
    {children}
  </OfferListCarouselTrack>

  if (size === 'page') {
    let itemsPerArrow = 1
    if (tileStyle === 'card-default' || tileStyle === 'card-opaque' || tileStyle === 'card-locked') {
      if (tileSize === 'small') {
        itemsPerArrow = 3
      } else if (tileSize === 'medium') {
        itemsPerArrow = 2
      }
    }

    return <FullWidthCarouselWithAnalytics
      itemsPerArrow={itemsPerArrow}
      snap="start"
      className={className}
      ref={carouselRef}
    >
      {track}
    </FullWidthCarouselWithAnalytics>
  }

  return <CardCarousel
    snap="start"
    className={className}
    ref={carouselRef}
  >
    {track}
  </CardCarousel>
}

export default React.memo(OfferListCarousel)
